import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { SUPPORTED_LANGS, DEFAULT_LANG, LANG_STORAGE_KEY, type Lang } from '@/i18n/config';
import { localizePath } from '@/i18n/paths';

/**
 * Rewrite the current URL for a different language.  Wiki, Field Guide,
 * topics, world-map and report URLs follow the path-prefix convention
 * (default lang = no prefix, others = `/<lang>`).  Switching language on one
 * of those pages must rewrite the URL — otherwise the page reads `lang` from
 * the route and silently flips i18n back on the next render.  Returns null
 * when the path has no localized twin — the caller falls back to plain
 * `i18n.changeLanguage()` then.
 */
function rewritePathForLang(
  pathname: string,
  search: string,
  hash: string,
  nextLang: Lang,
): string | null {
  const next = localizePath(pathname, nextLang);
  if (next === pathname) return null;
  return `${next}${search}${hash}`;
}

// Конфиг языков. short — фиксированной длины (2 буквы) → триггер не «прыгает» между сменами.
// label — родное название (endonym), оставляем на родном языке независимо от UI-локали.
const LANG_META: Record<Lang, { short: string; label: string }> = {
  en: { short: 'EN', label: 'English' },
  ru: { short: 'RU', label: 'Русский' },
};

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentCode = (SUPPORTED_LANGS.includes(i18n.language as Lang)
    ? i18n.language
    : DEFAULT_LANG) as Lang;
  const current = LANG_META[currentCode];

  // Клик вне / Esc → закрыть
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.switcherAria', {
          current: current.label,
          defaultValue: 'Current language: {{current}}. Change language',
        })}
        className="inline-flex min-h-[32px] items-center gap-1 rounded-md border border-[color:var(--color-ink-100)] px-2 py-1 text-sm font-medium text-[color:var(--color-ink-700)] hover:bg-[color:var(--color-paper-2)]"
      >
        {/* Глобус */}
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 opacity-80"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
        </svg>
        <span className="min-w-[1.5rem] text-center tabular-nums">{current.short}</span>
        {/* Chevron */}
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3 w-3 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('language.listAria', { defaultValue: 'Language picker' })}
          className="absolute right-0 top-full z-[2000] mt-1 min-w-[160px] overflow-hidden rounded-md border border-ink-100 bg-paper shadow-lg"
        >
          {SUPPORTED_LANGS.map((code) => {
            const meta = LANG_META[code];
            const isActive = code === currentCode;
            return (
              <li key={code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => {
                    // Persist the choice up-front so a failed
                    // `changeLanguage` (network, race with another
                    // component, browser-extension interference) still
                    // gets honoured on the next mount via the language
                    // detector cache.
                    try {
                      window.localStorage.setItem(LANG_STORAGE_KEY, code);
                    } catch {
                      // localStorage may be unavailable (private mode, quota).
                      // Carry on — i18n.changeLanguage below is the fallback.
                    }
                    // Wiki / guide / topics pages read `lang` from the URL.
                    // Rewrite the path first; i18n then syncs automatically
                    // via the page-mount effect.  For the rest of the site the
                    // URL has no language prefix, so we just change i18n.
                    const rewritten = rewritePathForLang(
                      location.pathname,
                      location.search,
                      location.hash,
                      code,
                    );
                    if (rewritten) {
                      navigate(rewritten, { replace: false });
                    }
                    // Always update i18n state, even for wiki pages — the
                    // page-mount effect on CountryHubPage/CountryTopicPage
                    // is idempotent and skips when languages already match.
                    // Doing it here too means dropdown-driven UI strings
                    // outside the wiki tree (header, footer) re-render
                    // synchronously instead of waiting for the route mount.
                    i18n.changeLanguage(code).catch((err) => {
                      // Surface failures in dev so a silent breakage on
                      // the language menu is debuggable.
                      console.error('[LanguageSwitcher] changeLanguage failed', err);
                    });
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
                    isActive
                      ? 'bg-paper-2 font-semibold text-ink-900'
                      : 'text-ink-700 hover:bg-paper-2'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-7 font-mono text-[11px] font-medium tracking-wide text-ink-500 tabular-nums">
                      {meta.short}
                    </span>
                    <span>{meta.label}</span>
                  </span>
                  {isActive && (
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4 text-terra-600"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
